import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'core/store';
import defaultConfig from './default-remote-config.json';

export interface RemoteConfigState {
  maintenanceScreenEnabled: boolean;
  displayWarranty: boolean;
  displayFatalErrorFilter: boolean;
  incidentCommunicationEnabled: boolean;
  enableEposArea: boolean;
  enableFavoritCompaniesFilter: boolean;
  enableGutter: boolean;
  getMarkersPollingInterval: number;
  numberOfCustomerWidgetsAllowed: number;
  dashboardWidgetsPollingInterval: number;
  numberOfUserDefinedWidgetsAllowed: number;
  feedbackUrl: string;
  dealersReport: string;
  displayStreamingMap: string;
  displayUserDefinedGroupedListWidgets: string;
  appStatusConfig: string;
}

export interface AppStatusConfig {
  statusMode: string;
  bannerMessage: string;
  bannerSeverity: string;
  lockdownTitle: string;
  lockdownMessage: string;
}

const defaultAppStatusConfig: AppStatusConfig = {
  statusMode: '',
  bannerMessage: '',
  bannerSeverity: '',
  lockdownTitle: '',
  lockdownMessage: '',
};

export const initialState: RemoteConfigState = {
  maintenanceScreenEnabled: defaultConfig.maintenance_screen_enabled === 'true',
  displayWarranty: defaultConfig.display_warranty === 'true',
  displayFatalErrorFilter: defaultConfig.display_fatal_error_filter === 'true',
  incidentCommunicationEnabled: defaultConfig.incident_communication_enabled === 'true',
  enableEposArea: defaultConfig.enable_epos_area === 'true',
  enableFavoritCompaniesFilter: defaultConfig.enable_favorit_companies_filter === 'true',
  enableGutter: defaultConfig.enable_gutter === 'true',
  getMarkersPollingInterval: Number(defaultConfig.get_markers_polling_interval),
  numberOfCustomerWidgetsAllowed: Number(defaultConfig.no_of_customer_widgets_allowed),
  dashboardWidgetsPollingInterval: Number(defaultConfig.dashboard_widgets_polling_interval),
  numberOfUserDefinedWidgetsAllowed: Number(defaultConfig.no_of_user_defined_widgets_allowed),
  feedbackUrl: defaultConfig.feedback_url,
  dealersReport: defaultConfig.dealers_report,
  displayStreamingMap: defaultConfig.display_streaming_map,
  displayUserDefinedGroupedListWidgets: defaultConfig.display_grouped_list_widgets,
  appStatusConfig: defaultConfig.app_status_config,
};

const slice = createSlice({
  name: 'remoteConfig',
  initialState,
  reducers: {
    setFlags: (_state, action: PayloadAction<RemoteConfigState>) => action.payload,
  },
});

export const { setFlags } = slice.actions;
export default slice.reducer;

const selectRemoteConfig = (state: RootState) => state.remoteConfig;

export const selectMaintenanceScreenEnabled = (state: RootState) => state.remoteConfig.maintenanceScreenEnabled;
export const selectDisplayWarranty = (state: RootState) => state.remoteConfig.displayWarranty;
export const selectDisplayFatalErrorFilter = (state: RootState) => state.remoteConfig.displayFatalErrorFilter;
export const selectIncidentCommunicationEnabled = (state: RootState) => state.remoteConfig.incidentCommunicationEnabled;
export const selectEnableEposArea = (state: RootState) => state.remoteConfig.enableEposArea;
export const selectEnableFavoritCompaniesFilter = (state: RootState) => state.remoteConfig.enableFavoritCompaniesFilter;
export const selectEnableGutter = (state: RootState) => state.remoteConfig.enableGutter;
export const selectGetMarkersPollingInterval = (state: RootState) => state.remoteConfig.getMarkersPollingInterval;
export const selectNumberOfCustomerWidgetsAllowed = (state: RootState) => state.remoteConfig.numberOfCustomerWidgetsAllowed;
export const selectDashboardWidgetsPollingInterval = (state: RootState) => state.remoteConfig.dashboardWidgetsPollingInterval;
export const selectNumberOfUserDefinedWidgetsAllowed = (state: RootState) => state.remoteConfig.numberOfUserDefinedWidgetsAllowed;
export const selectFeedbackUrl = (state: RootState) => state.remoteConfig.feedbackUrl;
export const selectDealersReport = (state: RootState) => state.remoteConfig.dealersReport;
export const selectDisplayStreamingMap = (state: RootState) => state.remoteConfig.displayStreamingMap;
export const selectDisplayUserDefinedGroupedListWidgets = (state: RootState) => state.remoteConfig.displayUserDefinedGroupedListWidgets;

export const selectAppStatusConfig = createSelector(
  [(state: RootState) => state.remoteConfig.appStatusConfig],
  (raw): AppStatusConfig => {
    if (!raw) {
      return defaultAppStatusConfig;
    }
    try {
      return { ...defaultAppStatusConfig, ...JSON.parse(raw) };
    } catch {
      return defaultAppStatusConfig;
    }
  },
);

export { selectRemoteConfig };
